import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, Loading, Empty, Plate, AnomalyBadge, ExportBtn, Field, NSelect, useFetch, AN_STATUS } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { LivFilters } from "@/pages/livraison/LivDashboard";
import { api, errMsg, fDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ORDER = ["nouvelle", "a_traiter", "intervention_programmee", "resolue"];

function EditDialog({ a, onClose, onSaved }) {
  const [status, setStatus] = useState(a.status);
  const [date, setDate] = useState(a.intervention_date || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/anomalies/${a.id}`, { status, note, intervention_date: date });
      toast.success("Anomalie mise à jour");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="anomaly-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{a.nature} — {a.plate}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 -mt-2">{fDate(a.date)} · Chauffeur : {a.driver_name} · Contrôlé par : {a.controller_name} · {a.description || "Sans description"}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Statut">
            <NSelect value={status} onChange={setStatus} testid="anomaly-status-select" options={ORDER.map((s) => ({ value: s, label: AN_STATUS[s][0] }))} />
          </Field>
          <Field label="Date d'intervention">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" data-testid="anomaly-intervention-date" />
          </Field>
        </div>
        <Field label="Note / intervention effectuée">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="anomaly-note-input" />
        </Field>
        <div className="border-t pt-3">
          <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-2">Historique</div>
          <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="anomaly-history">
            {a.history.map((h, i) => (
              <li key={i} className="text-xs flex items-center gap-2"><span className="text-slate-400 w-20">{fDate(h.date)}</span><AnomalyBadge v={h.status} /><span className="text-slate-600 truncate">{h.by}{h.note ? ` — ${h.note}` : ""}</span></li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="anomaly-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="anomaly-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Anomalies() {
  const [f, setF] = useState({ status: "ouvertes" });
  const [edit, setEdit] = useState(null);
  const { data, loading, setData } = useFetch("/anomalies", f);
  const { canEdit } = useAuth().user;
  const counts = ORDER.map((s) => [s, (data || []).filter((a) => a.status === s).length]);
  return (
    <div data-testid="anomalies-page">
      <PageHeader overline="Véhicules de livraison" title="Anomalies">
        <ExportBtn path="/export/anomalies" params={f} filename="anomalies.xlsx" testid="export-anomalies" />
      </PageHeader>
      <LivFilters f={f} setF={setF} testid="anomalies-filters" />
      <div className="flex flex-wrap gap-2 mb-4">
        {counts.map(([s, n]) => (
          <span key={s} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${AN_STATUS[s][1]}`}>{AN_STATUS[s][0]} · {n}</span>
        ))}
      </div>
      {loading ? <Loading /> : !data?.length ? <Empty text="Aucune anomalie pour ces filtres" /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto rise">
          <table className="tbl w-full" data-testid="anomalies-table">
            <thead><tr><th>Date</th><th>Véhicule</th><th>Chauffeur attribué</th><th>Contrôlé par</th><th>Nature</th><th>Description</th><th>Statut</th><th>Intervention</th><th></th></tr></thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id} data-testid={`anomaly-row-${a.id}`}>
                  <td>{fDate(a.date)}</td>
                  <td><Link to={`/livraison/vehicules/${a.vehicle_id}`}><Plate>{a.plate}</Plate></Link></td>
                  <td>{a.driver_name}</td><td className="text-slate-500">{a.controller_name}</td><td className="font-semibold">{a.nature}</td>
                  <td className="max-w-[240px] truncate text-slate-500">{a.description || "—"}</td>
                  <td><AnomalyBadge v={a.status} /></td><td>{fDate(a.intervention_date)}</td>
                  <td>{canEdit && <button onClick={() => setEdit(a)} className="text-xs font-bold text-[#E0830B] hover:underline" data-testid={`anomaly-edit-${a.id}`}>Traiter</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <EditDialog a={edit} onClose={() => setEdit(null)} onSaved={(n) => { setData(data.map((x) => (x.id === n.id ? n : x))); setEdit(null); }} />}
    </div>
  );
}
